<template>
    <VueTreeSelect ref="TreeSelect" v-bind="attrs" :key="treeKey" :class="{ 'is-valid': attrs.state === true, 'is-invalid': attrs.state === false }" v-on="listeners">
        <template v-for="slotName in Object.keys($slots)" #[slotName]>
            <slot :name="slotName"></slot>
        </template>
        <template v-for="slotName in Object.keys($scopedSlots)" #[slotName]="slotProps">
            <slot :name="slotName" v-bind="slotProps"></slot>
        </template>
        <template #before-list>
            <div v-if="multiple && options && options.length" class="p-2 custom-control custom-checkbox" @click.prevent="!isToggleAllDisabled() && toggleAll()">
                <input
                    id="checkbox-select-all"
                    type="checkbox"
                    class="custom-control-input"
                    :checked="isChecked()"
                    :indeterminate.prop="isIndeterminate()"
                    :disabled="isToggleAllDisabled()"
                >
                <label class="custom-control-label" for="checkbox-select-all">{{ $t('selectAll') }}</label>
            </div>
        </template>
    </VueTreeSelect>
</template>



<script>
    import md5 from 'md5';
    import { i18n } from '@/i18n/i18n';
    import VueTreeSelect from '@/components/common/VueTreeSelect.vue';

    export default {
        name: 'FormSelectTree',
        inheritAttrs: false,
        components: {
            VueTreeSelect,
        },
        props: {
            showCountValue: {
                type: Boolean,
                default: false,
            },
            preselect: {
                type: Boolean,
                default: false,
            },
            preventEmptySelection: {
                type: Boolean,
                default: false,
            },
            multiple: {
                type: Boolean,
                default: false,
            },
            disableBranchNodes: {
                type: Boolean,
                default: false,
            },
            async: {
                type: Boolean,
                default: false,
            },
            defaultOptions: {
                type: Boolean,
                default: false,
            },
            cacheOptions: {
                type: Boolean,
                default: false,
            },
            selectedOptionsLabel: {
                type: String,
                default: () => i18n.t('selectField.selectedOptions'),
            },
        },
        data() {
            return {
                options: this.$attrs.options,
                originalRenderMultiValueItems: null,
                originalClear: null,
                openValue: null,
                closeValue: null,
                flagPreselect: false,
                treeKey: 'tree',
                isEnforcingSelection: false, // flaga anty‑pętli podczas wymuszania wyboru
            };
        },
        computed: {
            attrs() {
                const attrs = {
                    clearAllText: this.$t('clearAll'),
                    clearValueText: this.$t('clearValue'),
                    loadingText: this.$t('loadingDots'),
                    noChildrenText: this.$t('noSubOptions'),
                    noOptionsText: this.$t('noOptionsAvailable'),
                    noResultsText: this.$t('noResultsFound'),
                    placeholder: this.$t('selectDots'),
                    retryText: this.$t('retry'),
                    retryTitle: this.$t('clickToRetry'),
                    searchPromptText: this.$t('typeToSearch'),
                    ...this.$attrs,
                    options: this.options,
                    multiple: this.multiple,
                    disableBranchNodes: this.disableBranchNodes,
                    async: this.async,
                    defaultOptions: this.defaultOptions,
                    cacheOptions: this.cacheOptions,
                };
                if (typeof this.$attrs['load-options'] === 'function') {
                    attrs['load-options'] = this.loadOptions;
                }
                return attrs;
            },
            listeners() {
                return {
                    ...this.$listeners,
                    open: this.open,
                    close: this.close,
                };
            },
            value() {
                if (!this.$refs.TreeSelect?.internalValue) return [];
                return this.$refs.TreeSelect.internalValue.slice(0, this.$refs.TreeSelect.limit).map(this.$refs.TreeSelect.getNode);
            },
        },
        watch: {
            '$attrs.options': {
                handler(newOptions, oldOptions) {
                    if (JSON.stringify(newOptions || []) !== JSON.stringify(oldOptions || [])) {
                        this.options = newOptions;
                        this.treeKey = md5(JSON.stringify(newOptions));
                    }
                },
            },
            options: {
                immediate: true,
                handler() {
                    if (this.preselect && this.options?.length && this.flagPreselect === false) {
                        this.flagPreselect = true;
                        this.$nextTick(() => {
                            this.preselectOptions();
                            this.$nextTick(() => {
                                const value = this.$refs.TreeSelect?.getValue() || null;
                                this.$emit('change', value);
                            });
                        });
                    }

                    // jeśli parent trzyma puste value i wymagamy niepusto, a opcje już są – wymuś wybór
                    if (this.preventEmptySelection && this.options?.length) {
                        const externalVal = this.$attrs.value;
                        if (this.isEmptyValue(externalVal) && this.isEmptyValue(this.$refs.TreeSelect?.getValue?.())) {
                            this.$nextTick(() => {
                                this.isEnforcingSelection = true;
                                this.preselectOptions();
                                this.$nextTick(() => {
                                    const enforced = this.$refs.TreeSelect?.getValue?.() || null;
                                    this.$emit('input', enforced);
                                    this.$emit('change', enforced);
                                    this.isEnforcingSelection = false;
                                });
                            });
                        }
                    }
                },
            },
            showCountValue() {
                this.setRenderMultiValueItems();
            },
            // reaguj na programistyczne wyzerowanie value przez rodzica
            '$attrs.value': {
                immediate: false,
                handler(newVal) {
                    if (!this.preventEmptySelection) return;
                    if (this.isEnforcingSelection) return;

                    if (this.isEmptyValue(newVal)) {
                        const enforce = () => {
                            if (!this.$refs.TreeSelect?.forest) return; // brak zainicjalizowanego drzewa
                            const before = this.$refs.TreeSelect?.getValue?.() || null;
                            this.isEnforcingSelection = true;
                            this.preselectOptions();
                            this.$nextTick(() => {
                                const enforced = this.$refs.TreeSelect?.getValue?.() || null;
                                if (this.hasChanged(before, enforced)) {
                                    this.$emit('input', enforced);
                                    this.$emit('change', enforced);
                                }
                                this.isEnforcingSelection = false;
                            });
                        };

                        if (this.options && this.options.length) {
                            enforce();
                        }
                        // jeśli opcji jeszcze nie ma – watcher 'options' to nadrobi
                    }
                },
            },
        },
        methods: {
            // helper do sprawdzania pustej wartości zewnętrznej
            isEmptyValue(val) {
                return val == null || (Array.isArray(val) && val.length === 0);
            },
            isToggleAllDisabled() {
                const anyLeaf = Object.values(this.$refs.TreeSelect.forest.nodeMap).some(item => item.isLeaf);
                if (this.$refs.TreeSelect.forest.selectedNodeIds.length) return false;
                if (this.disableBranchNodes && !anyLeaf) return true;
                return false;
            },
            toggleAll() {
                if (this.$refs.TreeSelect) {
                    if (this.isChecked()) {
                        this.deselectAll();
                    } else {
                        this.selectAll();
                    }
                }
            },
            selectAll() {
                const ids = this.getAvailableIds();
                this.$set(this.$refs.TreeSelect.forest, 'selectedNodeIds', ids);
                this.$refs.TreeSelect.buildForestState();
            },
            getAvailableIds() {
                let nodes = Object.values(this.$refs.TreeSelect.forest.nodeMap || {}).filter(node => !node.isDisabled);
                if (this.disableBranchNodes) {
                    nodes = nodes.filter(node => !node.isBranch);
                }
                const ids = nodes.map(node => node.id);
                return ids;
            },
            deselectAll() {
                this.$set(this.$refs.TreeSelect.forest, 'selectedNodeIds', []);
                this.$refs.TreeSelect.buildForestState();
            },
            preselectOptions() {
                if (this.multiple) {
                    this.selectAll();
                } else {
                    this.selectFirst();
                }
            },
            selectFirst() {
                this.$set(
                    this.$refs.TreeSelect.forest,
                    'selectedNodeIds',
                    Object.keys(this.$refs.TreeSelect.forest.nodeMap || [])
                        .map(Number)
                        .slice(0, 1)
                );
                this.$refs.TreeSelect.buildForestState();
            },
            isChecked() {
                const selectedCount = this.$refs.TreeSelect?.forest.selectedNodeIds.length;
                const availableCount = this.getAvailableIds().length;
                if (selectedCount && availableCount && selectedCount === availableCount) return true;
                return false;
            },
            isIndeterminate() {
                if (this.isChecked()) return false;
                const value = this.$refs.TreeSelect?.internalValue;
                if (value.length) return true;
                return false;
            },
            hasChanged(openValue, closeValue) {
                if (Array.isArray(openValue) && Array.isArray(closeValue)) {
                    const comparator = typeof openValue[0] === 'number' && typeof closeValue[0] === 'number' ? (a, b) => a - b : undefined;
                    const sortedOpen = [...openValue].sort(comparator);
                    const sortedClose = [...closeValue].sort(comparator);
                    return JSON.stringify(sortedOpen) !== JSON.stringify(sortedClose);
                }
                return openValue !== closeValue;
            },
            open(instanceId) {
                this.openValue = this.$refs.TreeSelect?.value || null;
                this.closeValue = null;
                this.$emit('open', this.openValue, instanceId);
            },
            close(value, instanceId) {
                if (this.preventEmptySelection && (!value || (Array.isArray(value) && !value.length))) {
                    this.preselectOptions();
                }
                this.closeValue = this.$refs.TreeSelect?.getValue() || null;
                if (this.hasChanged(this.openValue, this.closeValue)) {
                    this.$emit('change', this.closeValue, instanceId);
                }
                this.$emit('close', this.closeValue, instanceId);
                this.openValue = null;
                this.closeValue = null;
            },
            reBuildTree(clearMap = false) {
                if (clearMap) this.$refs.TreeSelect.forest.nodeMap = Object.create(null);
                this.$refs.TreeSelect.buildForestState();
                this.$refs.TreeSelect.initialize();
            },
            async loadOptions(params) {
                const { action, callback, parentNode } = params;
                const internalCallback = data => {
                    if (data instanceof Error) {
                        return callback(data);
                    }
                    if (action === 'LOAD_ROOT_OPTIONS') {
                        this.options = data;
                        return callback();
                    }
                    if (action === 'LOAD_CHILDREN_OPTIONS') {
                        parentNode.children = data;
                        this.reBuildTree();
                        return callback();
                    }
                    if (action === 'ASYNC_SEARCH') {
                        this.reBuildTree(true);
                        return callback(null, data);
                    }
                    return callback();
                };
                if (action === 'LOAD_CHILDREN_OPTIONS' && Array.isArray(parentNode.children)) {
                    return callback();
                }
                this.emitLoading(action, true);
                return this.$attrs['load-options']({ ...params, callback: internalCallback })
                    .then(internalCallback)
                    .catch(internalCallback)
                    .finally(() => {
                        this.emitLoading(action, false);
                    });
            },
            emitLoading(action, value) {
                if (action === 'LOAD_ROOT_OPTIONS') {
                    this.$emit('rootLoading', value);
                }
                if (action === 'LOAD_CHILDREN_OPTIONS') {
                    this.$emit('childrenLoading', value);
                }
                if (action === 'ASYNC_SEARCH') {
                    this.$emit('searchLoading', value);
                }
            },
            addNode(parentId, newNode) {
                const findAndInsert = nodes => {
                    // eslint-disable-next-line no-restricted-syntax
                    for (const node of nodes) {
                        if (node.id === parentId) {
                            if (!Array.isArray(node.children)) {
                                node.children = [];
                            }
                            node.children.push(newNode);
                            return newNode;
                        }
                        if (Array.isArray(node.children)) {
                            const found = findAndInsert(node.children);
                            if (found) {
                                return found;
                            }
                        }
                    }
                    return false;
                };
                const inserted = findAndInsert(this.options);
                return inserted;
            },
            removeNode(nodeId) {
                const findAndRemove = nodes => {
                    for (let i = 0; i < nodes.length; i++) {
                        // Jeśli to szukany węzeł, usuń go
                        if (nodes[i].id === nodeId) {
                            const removedNode = nodes.splice(i, 1);
                            return removedNode[0];
                        }
                        // W przeciwnym razie, jeśli węzeł ma dzieci, spróbuj usunąć w nich
                        if (Array.isArray(nodes[i].children)) {
                            const found = findAndRemove(nodes[i].children);
                            if (found) {
                                return found;
                            }
                        }
                    }
                    return false;
                };
                const removed = findAndRemove(this.options);
                return removed;
            },
            localRenderMultiValueItems() {
                const selectedNodeIds = this.$refs.TreeSelect?.forest.selectedNodeIds;
                const label = selectedNodeIds.length ? `${this.selectedOptionsLabel} (${selectedNodeIds.length})` : '';
                const inputValue = this.$refs.TreeSelect?.$refs.control?.$refs['value-container']?.$refs.input?.value;
                return this.$createElement('div', { key: 'MultiValueItemsCount', class: { 'vue-treeselect__multi-value-items-count': true, 'vue-treeselect__is-value': inputValue, 'vue-treeselect__is-empty': !inputValue } }, label);
            },
            setRenderMultiValueItems() {
                const vNodeValue = this.$refs.TreeSelect?.$refs.control?.$refs['value-container'];
                if (vNodeValue) {
                    this.originalRenderMultiValueItems = vNodeValue.renderMultiValueItems || null;
                    const currentFn = this.showCountValue ? this.localRenderMultiValueItems : this.originalRenderMultiValueItems;
                    vNodeValue.renderMultiValueItems = currentFn;
                    vNodeValue.$forceUpdate();
                }
            },
            setClear() {
                const vNodeControl = this.$refs.TreeSelect?.$refs.control;
                if (vNodeControl) {
                    this.originalClear = vNodeControl.instance.clear || null;
                    vNodeControl.instance.clear = this.localClear;
                }
            },
            localClear() {
                this.$refs.TreeSelect.$on('input', this.emitClearEvent);
                if (this.originalClear) {
                    this.originalClear.bind(this.$refs.TreeSelect.$refs.control.instance)();
                }
            },
            emitClearEvent(value) {
                if (this.preventEmptySelection) {
                    // po clear odtwórz wybór i emituj wartość niepustą
                    this.isEnforcingSelection = true;
                    this.preselectOptions();
                    this.$nextTick(() => {
                        const enforced = this.$refs.TreeSelect?.getValue?.() || null;
                        this.$emit('clear', enforced); // sygnalizujemy, że użytkownik kliknął "clear"
                        this.$emit('input', enforced);
                        this.$emit('change', enforced);
                        this.isEnforcingSelection = false;
                        this.$refs.TreeSelect.$off('input', this.emitClearEvent);
                    });
                    return;
                }

                // ścieżka jak dotąd
                this.$emit('clear', value);
                this.$emit('change', value);
                this.$refs.TreeSelect.$off('input', this.emitClearEvent);
            },
        },
        mounted() {
            this.setRenderMultiValueItems();
            this.setClear();

            // start z pustą wartością + wymaganie niepusto + gotowe opcje
            if (this.preventEmptySelection && this.isEmptyValue(this.$attrs.value) && this.options?.length) {
                this.$nextTick(() => {
                    if (this.isEmptyValue(this.$refs.TreeSelect?.getValue?.())) {
                        this.isEnforcingSelection = true;
                        this.preselectOptions();
                        this.$nextTick(() => {
                            const enforced = this.$refs.TreeSelect?.getValue?.() || null;
                            this.$emit('input', enforced);
                            this.$emit('change', enforced);
                            this.isEnforcingSelection = false;
                        });
                    }
                });
            }
        },
    };
</script>



<style lang="scss" scoped>
    @import 'bootstrap/scss/functions';
    @import 'bootstrap/scss/variables';
    @import 'bootstrap/scss/mixins';
    @import '@/design/custom/_variables.scss';

    .vue-treeselect {
        &.is-invalid {
            ::v-deep {
                .vue-treeselect__control {
                    border-color: $danger;
                }
            }
        }
        &.is-valid {
            ::v-deep {
                .vue-treeselect__control {
                    border-color: $success;
                }
            }
        }

        ::v-deep {
            &:not(.vue-treeselect--disabled) .vue-treeselect__control-arrow-container .vue-treeselect__control-arrow {
                color: #999 !important;
            }

            .vue-treeselect__control {
                position: relative;
                display: flex;
                gap: 10px;
                align-items: center;
                min-height: $input-height !important;
                padding: $input-padding-y 40px $input-padding-y $input-padding-x !important;
                font-family: $font-family-base !important;
                @include font-size($input-font-size);
                font-weight: $input-font-weight !important;
                line-height: $input-line-height !important;
                color: $input-color !important;
                background-color: $input-bg !important;
                background-clip: padding-box;
                border: $input-border-width solid $input-border-color;
            }

            .vue-treeselect__control-arrow-container {
                line-height: 16px;
                display: block;
                position: absolute;
                box-sizing: border-box;
                width: 40px;
                height: 38px;
                right: 1px;
                top: 1px;
                padding: 4px 8px;
                margin: 0;
                text-decoration: none;
                text-align: center;
                cursor: pointer;
                transition: transform 0.2s ease;

                &::before {
                    position: relative;
                    right: 0;
                    top: 65%;
                    color: #999;
                    margin-top: 4px;
                    border-style: solid;
                    border-width: 5px 5px 0 5px;
                    border-color: #999999 transparent transparent transparent;
                    content: '';
                }

                svg {
                    display: none;
                }
            }

            .vue-treeselect__x-container {
                right: 31px;
            }

            .vue-treeselect__value-container {
                display: flex;
            }

            .vue-treeselect__placeholder,
            .vue-treeselect__input-container,
            .vue-treeselect__input {
                padding: 0 !important;
                height: 22px;
                line-height: 22px;
            }

            .vue-treeselect__placeholder {
                color: $input-color !important;
                opacity: 0.52;
            }

            .vue-treeselect__multi-value,
            .vue-treeselect__single-value {
                display: flex;
                flex-wrap: nowrap;
                overflow: hidden;
                margin: 0;
                line-height: 22px;
                padding: 0;
            }

            .vue-treeselect__multi-value-item-container * {
                white-space: nowrap;
            }

            .vue-treeselect__control-arrow {
                transition: transform 0.3s ease;
            }

            .vue-treeselect__menu {
                overflow: auto;
                @include font-size($input-font-size);
            }

            .vue-treeselect__label {
                overflow: auto;
                text-overflow: unset;
            }

            .vue-treeselect__multi-value-items-count {
                white-space: nowrap;
                height: 22px;
                line-height: 22px;
                margin: auto 0;
                user-select: none;
            }

            .vue-treeselect__checkbox {
                &.vue-treeselect__checkbox--checked,
                &.vue-treeselect__checkbox--indeterminate {
                    background: $primary;
                    border-color: $primary;
                }
            }

            .vue-treeselect__option {
                &--highlight {
                    .vue-treeselect__label-container {
                        &:hover {
                            .vue-treeselect__checkbox {
                                border-color: $primary;
                            }
                        }
                    }
                }
            }

            .feather svg {
                fill: none;
            }
        }

        &.vue-treeselect--open {
            ::v-deep {
                .vue-treeselect__multi-value-items-count {
                    &.vue-treeselect__is-value {
                        display: none;
                    }
                    &.vue-treeselect__is-empty {
                        pointer-events: none;
                        position: absolute;
                        right: 0;
                        top: 0;
                        bottom: 0;
                        left: 0;
                        overflow: hidden;
                        color: $input-color !important;
                        opacity: 0.52;
                    }
                }
            }
        }
    }
</style>
